import React, { useState, useEffect, useCallback } from "react";
import PageHeader from "../../components/PageHeader";
import ApiCall from "../../utils/apicall";
import { useSelector } from "react-redux";
import { Helmet } from "react-helmet-async";
import { toast } from "react-toastify";
import {
  Container,
  TableHead,
  Typography,
  Dialog,
  TextField,
  DialogContent,
  DialogTitle,
  DialogActions,
  TableCell,
  Breadcrumbs,
  TableRow,
  TablePagination,
  Stack,
  Button,
  TableBody,
  Paper,
  TableContainer,
  Chip,
  Tabs,
  Tab,
  Box,
  Tooltip,
  IconButton,
  Divider,
  Alert,
  MenuItem,
  Grid,
} from "@mui/material";
import {
  HomeRounded,
  StarRounded,
  StarBorderRounded,
  CheckCircleOutline,
  PauseCircleOutline,
  RemoveCircleOutline,
  HourglassEmpty,
  VerifiedOutlined,
} from "@mui/icons-material";
import Iconify from "../../components/iconify";
import { AddCircleOutline, DeleteOutline } from "@mui/icons-material";
import { BrandedDialogHeader, SectionHeader, FIELD_SX, SFM_AMBER, SFM_SLATE } from "../../components/WeldAssure/BrandedForm";
import { hasPermission, PERMISSIONS } from "../../utils/hasPermission";
import VendorImportDialog from "../../components/vendors/VendorImportDialog";

import SmartTable from "../../components/SmartTable/SmartTable";
// ─── constants ───────────────────────────────────────────────────────────────

const AVL_TABS = [
  { label: "All Vendors", value: "" },
  { label: "Pending", value: "pending" },
  { label: "Approved (AVL)", value: "approved" },
  { label: "Suspended", value: "suspended" },
  { label: "Removed", value: "removed" },
];

const STATUS_CONFIG = {
  pending:   { label: "Pending Review", color: "warning",  icon: <HourglassEmpty sx={{ fontSize: 14 }} /> },
  approved:  { label: "Approved — AVL", color: "success",  icon: <VerifiedOutlined sx={{ fontSize: 14 }} /> },
  suspended: { label: "Suspended",      color: "error",    icon: <PauseCircleOutline sx={{ fontSize: 14 }} /> },
  removed:   { label: "Removed",        color: "default",  icon: <RemoveCircleOutline sx={{ fontSize: 14 }} /> },
};

const RATING_LABELS = {
  1: "Poor",
  2: "Fair",
  3: "Acceptable",
  4: "Good",
  5: "Excellent",
};

const VENDOR_TYPES = [
  { value: "MATERIAL_SUPPLIER", label: "Material Supplier" },
  { value: "SUBCONTRACT_FAB",   label: "Subcontract Fabrication" },
  { value: "NDT_INSPECTION",    label: "NDT / Inspection Service" },
  { value: "COATINGS",          label: "Coatings / Galvanizing" },
  { value: "CALIBRATION",       label: "Calibration Service" },
  { value: "FASTENER",          label: "Fastener Supplier" },
  { value: "CONSUMABLES",       label: "Welding Consumables" },
  { value: "OTHER",             label: "Other" },
];

const REL_TYPES = [
  "Parent Company", "Subsidiary", "Primary Contact", "Quality Contact",
  "Sales Representative", "Subcontractor", "Affiliated Vendor", "Other",
];

const EMPTY_FORM = {
  vendor_name: "",
  vendor_type: "",
  contact_name: "",
  office_phone: "",
  phone: "",
  email: "",
  website: "",
  address: "",
  address_line2: "",
  city: "",
  state: "",
  postal_code: "",
  country: "",
  scope_of_supply: "",
  certifications: "",
  qualification_notes: "",
  next_review_date: "",
  review_frequency_months: "",
  tax_id: "",
  duns: "",
  cage_code: "",
  relationships: [],
};

const VendorRelationships = ({ value = [], onChange }) => {
  const rows = Array.isArray(value) ? value : [];
  const update = (i, key, v) => { const next = rows.map((r, idx) => idx === i ? { ...r, [key]: v } : r); onChange(next); };
  const add = () => onChange([...rows, { type: "Primary Contact", name: "" }]);
  const remove = (i) => onChange(rows.filter((_, idx) => idx !== i));
  return (
    <Box>
      {rows.length === 0 && (
        <Typography variant="body2" sx={{ color: "text.disabled", mb: 1 }}>
          No related parties yet — link parent companies, key contacts, or subcontractors.
        </Typography>
      )}
      <Stack spacing={1.25}>
        {rows.map((r, i) => (
          <Stack key={i} direction="row" spacing={1.25} alignItems="center">
            <TextField select size="small" label="Relationship" value={r.type || ""}
              onChange={(e) => update(i, "type", e.target.value)} sx={{ ...FIELD_SX, width: 220 }}>
              {REL_TYPES.map((t) => <MenuItem key={t} value={t}>{t}</MenuItem>)}
            </TextField>
            <TextField size="small" label="Name" fullWidth value={r.name || ""}
              onChange={(e) => update(i, "name", e.target.value)} sx={FIELD_SX} />
            <IconButton onClick={() => remove(i)} sx={{ color: "error.main" }}><DeleteOutline /></IconButton>
          </Stack>
        ))}
      </Stack>
      <Button onClick={add} startIcon={<AddCircleOutline />} size="small"
        sx={{ mt: 1.25, color: SFM_AMBER, fontWeight: 600, textTransform: "none" }}>
        Add relationship
      </Button>
    </Box>
  );
};

const VendorForm = ({ formData, setFormData, formErrors = {} }) => {
  const set = (k) => (e) => setFormData({ ...formData, [k]: e.target.value });
  return (
    <Box sx={{ px: 3, py: 2.5 }}>
      <SectionHeader>Vendor</SectionHeader>
      <Grid container spacing={2}>
        <Grid item xs={12} sm={7}>
          <TextField label="Vendor Name *" fullWidth value={formData.vendor_name}
            onChange={set("vendor_name")} error={!!formErrors.vendor_name}
            helperText={formErrors.vendor_name} sx={FIELD_SX} />
        </Grid>
        <Grid item xs={12} sm={5}>
          <TextField select label="Vendor Type" fullWidth value={formData.vendor_type}
            onChange={set("vendor_type")} sx={FIELD_SX}>
            <MenuItem value=""><em>— Select —</em></MenuItem>
            {VENDOR_TYPES.map((t) => <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>)}
          </TextField>
        </Grid>
      </Grid>

      <SectionHeader>Primary Contact</SectionHeader>
      <Grid container spacing={2}>
        <Grid item xs={12} sm={6}>
          <TextField label="Contact Name" fullWidth value={formData.contact_name}
            onChange={set("contact_name")} sx={FIELD_SX} />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField label="Website" fullWidth value={formData.website}
            onChange={set("website")} placeholder="https://" sx={FIELD_SX} />
        </Grid>
        <Grid item xs={12} sm={4}>
          <TextField label="Office Phone" fullWidth value={formData.office_phone}
            onChange={set("office_phone")} sx={FIELD_SX} />
        </Grid>
        <Grid item xs={12} sm={4}>
          <TextField label="Mobile / Direct" fullWidth value={formData.phone}
            onChange={set("phone")} sx={FIELD_SX} />
        </Grid>
        <Grid item xs={12} sm={4}>
          <TextField label="Email" fullWidth value={formData.email}
            onChange={set("email")} sx={FIELD_SX} />
        </Grid>
      </Grid>

      <SectionHeader>Address</SectionHeader>
      <Grid container spacing={2}>
        <Grid item xs={12} sm={8}>
          <TextField label="Street Address" fullWidth value={formData.address}
            onChange={set("address")} sx={FIELD_SX} />
        </Grid>
        <Grid item xs={12} sm={4}>
          <TextField label="Suite / Unit" fullWidth value={formData.address_line2}
            onChange={set("address_line2")} sx={FIELD_SX} />
        </Grid>
        <Grid item xs={12} sm={5}>
          <TextField label="City" fullWidth value={formData.city}
            onChange={set("city")} sx={FIELD_SX} />
        </Grid>
        <Grid item xs={6} sm={3}>
          <TextField label="State / Province" fullWidth value={formData.state}
            onChange={set("state")} sx={FIELD_SX} />
        </Grid>
        <Grid item xs={6} sm={2}>
          <TextField label="ZIP / Postal" fullWidth value={formData.postal_code}
            onChange={set("postal_code")} sx={FIELD_SX} />
        </Grid>
        <Grid item xs={12} sm={2}>
          <TextField label="Country" fullWidth value={formData.country}
            onChange={set("country")} sx={FIELD_SX} />
        </Grid>
      </Grid>

      <SectionHeader>Qualification &amp; Approval</SectionHeader>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <TextField label="Scope of Supply" fullWidth multiline rows={2}
            value={formData.scope_of_supply} onChange={set("scope_of_supply")}
            helperText="What this vendor is approved to supply (materials, services, processes)" sx={FIELD_SX} />
        </Grid>
        <Grid item xs={12}>
          <TextField label="Certifications" fullWidth multiline rows={2}
            value={formData.certifications} onChange={set("certifications")}
            helperText="AISC, ISO 9001, ASME, NADCAP, mill cert capability, etc." sx={FIELD_SX} />
        </Grid>
        <Grid item xs={12}>
          <TextField label="Qualification Notes" fullWidth multiline rows={2}
            value={formData.qualification_notes} onChange={set("qualification_notes")}
            helperText="AISC GR §4.15 / ISO 9001 §8.4 supplier evaluation basis" sx={FIELD_SX} />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField label="Next Review Due Date" type="date" fullWidth
            InputLabelProps={{ shrink: true }} value={formData.next_review_date}
            onChange={set("next_review_date")} sx={FIELD_SX} />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField label="Review Frequency (months)" type="number" fullWidth
            value={formData.review_frequency_months} onChange={set("review_frequency_months")}
            placeholder="12" sx={FIELD_SX} />
        </Grid>
      </Grid>

      <SectionHeader>Identifiers</SectionHeader>
      <Grid container spacing={2}>
        <Grid item xs={12} sm={4}>
          <TextField label="Tax ID / EIN" fullWidth value={formData.tax_id}
            onChange={set("tax_id")} sx={FIELD_SX} />
        </Grid>
        <Grid item xs={12} sm={4}>
          <TextField label="DUNS" fullWidth value={formData.duns}
            onChange={set("duns")} sx={FIELD_SX} />
        </Grid>
        <Grid item xs={12} sm={4}>
          <TextField label="CAGE Code" fullWidth value={formData.cage_code}
            onChange={set("cage_code")} helperText="Federal supply chain" sx={FIELD_SX} />
        </Grid>
      </Grid>

      <SectionHeader>Relationships</SectionHeader>
      <VendorRelationships value={formData.relationships}
        onChange={(rel) => setFormData({ ...formData, relationships: rel })} />
    </Box>
  );
};

// ─── sub-components ──────────────────────────────────────────────────────────

const StarRating = ({ value, interactive = false, onChange }) => (
  <Stack direction="row" spacing={0} alignItems="center">
    {[1, 2, 3, 4, 5].map((star) =>
      interactive ? (
        <IconButton
          key={star}
          size="small"
          onClick={() => onChange(star)}
          sx={{ p: 0.25 }}
        >
          {star <= (value || 0)
            ? <StarRounded sx={{ color: "#f59e0b", fontSize: 22 }} />
            : <StarBorderRounded sx={{ color: "#d1d5db", fontSize: 22 }} />}
        </IconButton>
      ) : (
        <Box key={star}>
          {star <= (value || 0)
            ? <StarRounded sx={{ color: "#f59e0b", fontSize: 18 }} />
            : <StarBorderRounded sx={{ color: "#e5e7eb", fontSize: 18 }} />}
        </Box>
      )
    )}
    {value ? (
      <Typography variant="caption" sx={{ ml: 0.5, color: "text.secondary" }}>
        {RATING_LABELS[value]}
      </Typography>
    ) : null}
  </Stack>
);

const AVLStatusChip = ({ status }) => {
  const cfg = STATUS_CONFIG[status] || STATUS_CONFIG.pending;
  return (
    <Chip
      label={cfg.label}
      color={cfg.color}
      size="small"
      icon={cfg.icon}
      variant="outlined"
    />
  );
};

// ─── main component ──────────────────────────────────────────────────────────

const Vendor = () => {
  const { loginUser } = useSelector((state) => state.userSlice);
  const { permissions: userPermissions } = loginUser.decodedToken;
  const canAdd    = hasPermission(userPermissions, PERMISSIONS.ADD_VENDOR);
  const canEdit   = hasPermission(userPermissions, PERMISSIONS.EDIT_VENDOR);
  const canDelete = hasPermission(userPermissions, PERMISSIONS.DELETE_VENDOR);

  // ── list state
  const [vendors, setVendors] = useState([]);
  const [filterName, setFilterName] = useState("");
  const [activeTab, setActiveTab] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // ── modal states
  const [addOpen, setAddOpen]       = useState(false);
  const [editOpen, setEditOpen]     = useState(false);
  const [approveOpen, setApproveOpen] = useState(false);
  const [suspendOpen, setSuspendOpen] = useState(false);
  const [removeOpen, setRemoveOpen]   = useState(false);
  const [rateOpen, setRateOpen]       = useState(false);
  const [deleteOpen, setDeleteOpen]   = useState(false);
  const [importOpen, setImportOpen]   = useState(false);

  // ── working record & form data
  const [selectedVendor, setSelectedVendor] = useState(null);
  const [formData, setFormData]   = useState(EMPTY_FORM);
  const [formErrors, setFormErrors] = useState({});

  // ── approve-modal state
  const [approveData, setApproveData] = useState({
    rating: 0, rating_notes: "", qualification_notes: "", next_review_date: "",
  });

  // ── suspend/remove state
  const [suspendReason, setSuspendReason]   = useState("");
  const [removalReason, setRemovalReason]   = useState("");

  // ── rate modal state
  const [rateData, setRateData] = useState({
    rating: 0, rating_notes: "", next_review_date: "",
  });

  // ─── fetch ─────────────────────────────────────────────────────────────────

  const fetchVendors = useCallback(async () => {
    try {
      const params = {};
      if (activeTab) params.avl_status = activeTab;
      if (filterName) params.vendor_name = filterName;
      const res = await ApiCall.get("/vendor", { params });
      setVendors(res.data.data.vendors || []);
    } catch (err) {
      console.error("Error fetching vendors:", err);
    }
  }, [activeTab, filterName]);

  useEffect(() => { fetchVendors(); }, [fetchVendors]);

  // ─── form helpers ──────────────────────────────────────────────────────────

  const openAdd = () => {
    setFormData(EMPTY_FORM);
    setFormErrors({});
    setAddOpen(true);
  };

  const openEdit = (vendor) => {
    setSelectedVendor(vendor);
    setFormData({
      vendor_name: vendor.vendor_name || "",
      vendor_type: vendor.vendor_type || "",
      contact_name: vendor.contact_name || "",
      office_phone: vendor.office_phone || "",
      phone: vendor.phone || "",
      email: vendor.email || "",
      website: vendor.website || "",
      address: vendor.address || "",
      address_line2: vendor.address_line2 || "",
      city: vendor.city || "",
      state: vendor.state || "",
      postal_code: vendor.postal_code || "",
      country: vendor.country || "",
      scope_of_supply: vendor.scope_of_supply || "",
      certifications: vendor.certifications || "",
      qualification_notes: vendor.qualification_notes || "",
      next_review_date: vendor.next_review_date || "",
      review_frequency_months: vendor.review_frequency_months || "",
      tax_id: vendor.tax_id || "",
      duns: vendor.duns || "",
      cage_code: vendor.cage_code || "",
      relationships: Array.isArray(vendor.relationships) ? vendor.relationships : [],
    });
    setFormErrors({});
    setEditOpen(true);
  };

  const openApprove = (vendor) => {
    setSelectedVendor(vendor);
    setApproveData({
      rating: vendor.rating || 0,
      rating_notes: vendor.rating_notes || "",
      qualification_notes: vendor.qualification_notes || "",
      next_review_date: "",
    });
    setApproveOpen(true);
  };

  const openSuspend = (vendor) => {
    setSelectedVendor(vendor);
    setSuspendReason("");
    setSuspendOpen(true);
  };

  const openRemove = (vendor) => {
    setSelectedVendor(vendor);
    setSuspendReason("");
    setRemoveOpen(true);
  };

  const openRate = (vendor) => {
    setSelectedVendor(vendor);
    setRateData({
      rating: vendor.rating || 0,
      rating_notes: vendor.rating_notes || "",
      next_review_date: "",
    });
    setRateOpen(true);
  };

  const closeAll = () => {
    setAddOpen(false);
    setEditOpen(false);
    setApproveOpen(false);
    setSuspendOpen(false);
    setRemoveOpen(false);
    setRateOpen(false);
    setDeleteOpen(false);
    setSelectedVendor(null);
    setFormErrors({});
    setSuspendReason("");
    setRemovalReason("");
  };

  const validateForm = (data) => {
    const errors = {};
    if (!data.vendor_name?.trim()) errors.vendor_name = "Vendor name is required";
    return errors;
  };

  // ─── CRUD handlers ─────────────────────────────────────────────────────────

  const handleAdd = async () => {
    const errors = validateForm(formData);
    if (Object.keys(errors).length) { setFormErrors(errors); return; }
    try {
      await ApiCall.post("/vendor", formData);
      toast.success("Vendor added successfully");
      closeAll();
      fetchVendors();
    } catch (err) {
      toast.error("Failed to add vendor");
    }
  };

  const handleUpdate = async () => {
    const errors = validateForm(formData);
    if (Object.keys(errors).length) { setFormErrors(errors); return; }
    try {
      await ApiCall.put(`/vendor/${selectedVendor.id}`, formData);
      toast.success("Vendor updated successfully");
      closeAll();
      fetchVendors();
    } catch (err) {
      toast.error("Failed to update vendor");
    }
  };

  const handleDelete = async () => {
    try {
      await ApiCall.delete(`/vendor/${selectedVendor.id}`);
      toast.success("Vendor deleted");
      closeAll();
      fetchVendors();
    } catch (err) {
      toast.error("Failed to delete vendor");
    }
  };

  // ─── AVL action handlers ───────────────────────────────────────────────────

  const handleApprove = async () => {
    try {
      await ApiCall.patch(`/vendor/${selectedVendor.id}/approve`, approveData);
      toast.success(`${selectedVendor.vendor_name} approved and added to AVL`);
      closeAll();
      fetchVendors();
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to approve vendor");
    }
  };

  const handleSuspend = async () => {
    if (!suspendReason.trim()) {
      toast.error("A reason is required to suspend a vendor");
      return;
    }
    try {
      await ApiCall.patch(`/vendor/${selectedVendor.id}/suspend`, { suspend_reason: suspendReason });
      toast.warning(`${selectedVendor.vendor_name} suspended from AVL`);
      closeAll();
      fetchVendors();
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to suspend vendor");
    }
  };

  const handleRemoveFromAVL = async () => {
    if (!removalReason.trim()) {
      toast.error("A reason is required");
      return;
    }
    try {
      await ApiCall.patch(`/vendor/${selectedVendor.id}/remove-from-avl`, { removal_reason: removalReason });
      toast.info(`${selectedVendor.vendor_name} removed from AVL`);
      closeAll();
      fetchVendors();
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to remove vendor from AVL");
    }
  };

  const handleRate = async () => {
    if (!rateData.rating || rateData.rating < 1) {
      toast.error("Please select a star rating (1–5)");
      return;
    }
    try {
      await ApiCall.patch(`/vendor/${selectedVendor.id}/rate`, rateData);
      toast.success("Rating saved");
      closeAll();
      fetchVendors();
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to save rating");
    }
  };

  // ─── table display ─────────────────────────────────────────────────────────

  const displayed = vendors.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  // ─── render ────────────────────────────────────────────────────────────────

  return (
    <>
      <Helmet><title>Vendors | SFM</title></Helmet>

      <Container maxWidth="xl">
        {/* Header */}
        <PageHeader
          breadcrumbs={[{ label: "Vendors" }]}
          title="Vendors"
          action={<Stack direction="row" spacing={1}>
            {canAdd && (
              <Button
                variant="outlined"
                startIcon={<Iconify icon="eva:upload-fill" />}
                onClick={() => setImportOpen(true)}
              >
                Import List
              </Button>
            )}
            {canAdd && (
              <Button variant="contained" startIcon={<Iconify icon="eva:plus-fill" />} onClick={openAdd}>
                Add Vendor
              </Button>
            )}
          </Stack>}
          guide={{
            why: "Buy only from suppliers you've qualified — keep an approved vendor list (AVL).",
            who: "Purchasing and the QM, who maintain the approved vendor list.",
            what: "Your approved vendor list: suppliers, fabricators, NDT, coatings, and calibration services, with each one's qualification and rating.",
            how: "Add a vendor, record their qualification and scope of supply, then approve them onto the AVL.",
            when: "Before issuing a purchase order — and re-review approved vendors on schedule.",
            standard: "AISC GR §4.15 · ISO 9001:2015 §8.4",
          }}
        />

        {/* AVL status tabs */}
        <Paper sx={{ mb: 2 }}>
          <Tabs
            value={activeTab}
            onChange={(_, val) => { setActiveTab(val); setPage(0); }}
            variant="scrollable"
            scrollButtons="auto"
          >
            {AVL_TABS.map((t) => (
              <Tab key={t.value} label={t.label} value={t.value} />
            ))}
          </Tabs>
        </Paper>

        {/* Search + table */}
        <Paper>
          <Box sx={{ p: 2, pb: 0 }}>
            <TextField
              label="Search vendors…"
              variant="outlined"
              size="small"
              value={filterName}
              onChange={(e) => { setFilterName(e.target.value); setPage(0); }}
              sx={{ width: 280 }}
            />
          </Box>

          <TableContainer>
            <SmartTable tableId="vendors" lockedColumns={["Vendor Name", "AVL Status"]}>
              <TableHead>
                <TableRow>
                  <TableCell>Vendor Name</TableCell>
                  <TableCell>Contact</TableCell>
                  <TableCell>AVL Status</TableCell>
                  <TableCell>Rating</TableCell>
                  <TableCell>Last Review</TableCell>
                  <TableCell>Next Review</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {displayed.map((vendor) => {
                  const status = vendor.avl_status || "pending";
                  const overdue =
                    vendor.next_review_date &&
                    new Date(vendor.next_review_date) < new Date();

                  return (
                    <TableRow key={vendor.id} hover>
                      <TableCell>
                        <Typography variant="body2" fontWeight={600}>
                          {vendor.vendor_name}
                        </Typography>
                        {vendor.address && (
                          <Typography variant="caption" color="text.secondary">
                            {vendor.address}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">{vendor.contact_name || "—"}</Typography>
                        {vendor.email && (
                          <Typography variant="caption" color="text.secondary" display="block">
                            {vendor.email}
                          </Typography>
                        )}
                        {vendor.phone && (
                          <Typography variant="caption" color="text.secondary" display="block">
                            {vendor.phone}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Stack spacing={0.5} alignItems="flex-start">
                          <AVLStatusChip status={status} />
                          {status === "suspended" && vendor.suspend_reason && (
                            <Tooltip title={vendor.suspend_reason}>
                              <Typography variant="caption" color="error" sx={{ cursor: "help", maxWidth: 160, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", display: "block" }}>
                                {vendor.suspend_reason}
                              </Typography>
                            </Tooltip>
                          )}
                          {status === "approved" && vendor.approver && (
                            <Typography variant="caption" color="text.secondary">
                              by {vendor.approver.firstName} {vendor.approver.lastName}
                            </Typography>
                          )}
                        </Stack>
                      </TableCell>
                      <TableCell>
                        <StarRating value={vendor.rating} />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {vendor.last_review_date
                            ? new Date(vendor.last_review_date).toLocaleDateString()
                            : "—"}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        {vendor.next_review_date ? (
                          <Chip
                            label={new Date(vendor.next_review_date).toLocaleDateString()}
                            size="small"
                            color={overdue ? "error" : "default"}
                            variant={overdue ? "filled" : "outlined"}
                          />
                        ) : "—"}
                      </TableCell>
                      <TableCell align="right">
                        <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                          {/* Rate — only for approved vendors */}
                          {status === "approved" && canEdit && (
                            <Tooltip title="Update Rating">
                              <IconButton size="small" color="warning" onClick={() => openRate(vendor)}>
                                <StarRounded fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}

                          {/* Approve — for pending or suspended */}
                          {(status === "pending" || status === "suspended") && canEdit && (
                            <Tooltip title="Approve for AVL">
                              <IconButton size="small" color="success" onClick={() => openApprove(vendor)}>
                                <CheckCircleOutline fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}

                          {/* Suspend — only for approved */}
                          {status === "approved" && canEdit && (
                            <Tooltip title="Suspend from AVL">
                              <IconButton size="small" color="error" onClick={() => openSuspend(vendor)}>
                                <PauseCircleOutline fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}

                          {/* Remove — for approved or suspended */}
                          {(status === "approved" || status === "suspended") && canEdit && (
                            <Tooltip title="Remove from AVL">
                              <IconButton size="small" onClick={() => openRemove(vendor)}>
                                <RemoveCircleOutline fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}

                          {/* Edit */}
                          {canEdit && (
                            <Tooltip title="Edit vendor details">
                              <IconButton size="small" onClick={() => openEdit(vendor)}>
                                <Iconify icon="eva:edit-fill" width={18} />
                              </IconButton>
                            </Tooltip>
                          )}

                          {/* Delete */}
                          {canDelete && (
                            <Tooltip title="Delete vendor">
                              <IconButton
                                size="small"
                                sx={{ color: "error.main" }}
                                onClick={() => { setSelectedVendor(vendor); setDeleteOpen(true); }}
                              >
                                <Iconify icon="eva:trash-2-outline" width={18} />
                              </IconButton>
                            </Tooltip>
                          )}
                        </Stack>
                      </TableCell>
                    </TableRow>
                  );
                })}

                {vendors.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                      <Typography color="text.secondary">
                        {activeTab
                          ? `No ${STATUS_CONFIG[activeTab]?.label || activeTab} vendors`
                          : "No vendors yet — click Add Vendor to get started"}
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </SmartTable>
          </TableContainer>

          <TablePagination
            rowsPerPageOptions={[10, 25, 50]}
            component="div"
            count={vendors.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={(_, p) => setPage(p)}
            onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
          />
        </Paper>
      </Container>

      {/* ── ADD VENDOR MODAL ──────────────────────────────────────────────── */}
      <Dialog open={addOpen} onClose={closeAll} maxWidth="md" fullWidth>
        <BrandedDialogHeader title="Add New Vendor" subtitle="Approved Vendor List · AISC GR §4.15 / ISO 9001 §8.4" code="AVL" onClose={closeAll} />
        <DialogContent sx={{ p: 0 }}>
          <VendorForm formData={formData} setFormData={setFormData} formErrors={formErrors} />
        </DialogContent>
        <DialogActions>
          <Button onClick={closeAll}>Cancel</Button>
          <Button onClick={handleAdd} variant="contained">Add Vendor</Button>
        </DialogActions>
      </Dialog>

      {/* ── EDIT VENDOR MODAL ─────────────────────────────────────────────── */}
      <Dialog open={editOpen} onClose={closeAll} maxWidth="md" fullWidth>
        <BrandedDialogHeader title="Edit Vendor" subtitle="Approved Vendor List · AISC GR §4.15 / ISO 9001 §8.4" code="AVL" onClose={closeAll} />
        <DialogContent sx={{ p: 0 }}>
          <VendorForm formData={formData} setFormData={setFormData} formErrors={formErrors} />
        </DialogContent>
        <DialogActions>
          <Button onClick={closeAll}>Cancel</Button>
          <Button onClick={handleUpdate} variant="contained">Save Changes</Button>
        </DialogActions>
      </Dialog>

      {/* ── APPROVE MODAL ─────────────────────────────────────────────────── */}
      <Dialog open={approveOpen} onClose={closeAll} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Stack direction="row" alignItems="center" spacing={1}>
            <CheckCircleOutline color="success" />
            <span>Approve Vendor for AVL</span>
          </Stack>
        </DialogTitle>
        <DialogContent>
          <Alert severity="info" sx={{ mb: 2 }}>
            Approving <strong>{selectedVendor?.vendor_name}</strong> will add them to the Approved Vendor List. They may then be assigned to Purchase Orders.
          </Alert>
          <Stack spacing={2}>
            <Box>
              <Typography variant="body2" gutterBottom sx={{ fontWeight: 600 }}>
                Initial Rating
              </Typography>
              <StarRating value={approveData.rating} interactive onChange={(v) => setApproveData({ ...approveData, rating: v })} />
              <Typography variant="caption" color="text.secondary">
                {approveData.rating ? `${RATING_LABELS[approveData.rating]} (${approveData.rating}/5)` : "No rating selected — can be set later"}
              </Typography>
            </Box>
            <TextField label="Qualification Notes" fullWidth multiline rows={3}
              value={approveData.qualification_notes}
              onChange={(e) => setApproveData({ ...approveData, qualification_notes: e.target.value })}
              helperText="Certifications held, capabilities, audit results, approval basis" />
            <TextField label="Rating Notes" fullWidth multiline rows={2}
              value={approveData.rating_notes}
              onChange={(e) => setApproveData({ ...approveData, rating_notes: e.target.value })}
              helperText="Basis for star rating — on-time delivery, quality, responsiveness" />
            <TextField label="Next Scheduled Review Date" type="date" fullWidth
              InputLabelProps={{ shrink: true }} value={approveData.next_review_date}
              onChange={(e) => setApproveData({ ...approveData, next_review_date: e.target.value })}
              helperText="Recommended: 12 months from today" />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeAll}>Cancel</Button>
          <Button onClick={handleApprove} variant="contained" color="success">
            Approve &amp; Add to AVL
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── SUSPEND MODAL ─────────────────────────────────────────────────── */}
      <Dialog open={suspendOpen} onClose={closeAll} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Stack direction="row" alignItems="center" spacing={1}>
            <PauseCircleOutline color="error" />
            <span>Suspend Vendor from AVL</span>
          </Stack>
        </DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            Suspending <strong>{selectedVendor?.vendor_name}</strong> will prevent them from being assigned to new Purchase Orders. Existing POs are not affected.
          </Alert>
          <TextField
            label="Reason for Suspension *"
            fullWidth multiline rows={3}
            value={suspendReason}
            onChange={(e) => setSuspendReason(e.target.value)}
            helperText="Required — this reason is recorded in the audit trail"
            error={!suspendReason.trim() && suspendOpen}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={closeAll}>Cancel</Button>
          <Button onClick={handleSuspend} variant="contained" color="error">
            Suspend Vendor
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── REMOVE FROM AVL MODAL ─────────────────────────────────────────── */}
      <Dialog open={removeOpen} onClose={closeAll} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Stack direction="row" alignItems="center" spacing={1}>
            <RemoveCircleOutline />
            <span>Remove Vendor from AVL</span>
          </Stack>
        </DialogTitle>
        <DialogContent>
          <Alert severity="error" sx={{ mb: 2 }}>
            Removing <strong>{selectedVendor?.vendor_name}</strong> from the AVL is a permanent disqualification. They cannot be re-approved without a new evaluation.
          </Alert>
          <TextField
            label="Reason for Removal *"
            fullWidth multiline rows={3}
            value={removalReason}
            onChange={(e) => setRemovalReason(e.target.value)}
            helperText="Required — this reason is permanently recorded"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={closeAll}>Cancel</Button>
          <Button onClick={handleRemoveFromAVL} variant="contained" color="error">
            Remove from AVL
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── RATE MODAL ────────────────────────────────────────────────────── */}
      <Dialog open={rateOpen} onClose={closeAll} maxWidth="sm" fullWidth>
        <DialogTitle>Rate Vendor — {selectedVendor?.vendor_name}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Box>
              <Typography variant="body2" gutterBottom sx={{ fontWeight: 600 }}>
                Performance Rating *
              </Typography>
              <StarRating value={rateData.rating} interactive onChange={(v) => setRateData({ ...rateData, rating: v })} />
              {rateData.rating > 0 && (
                <Typography variant="body2" sx={{ mt: 0.5, color: "text.secondary" }}>
                  {rateData.rating}/5 — {RATING_LABELS[rateData.rating]}
                </Typography>
              )}
            </Box>
            <Divider />
            <Typography variant="caption" color="text.secondary">
              Rating criteria: on-time delivery, material quality, CMTR accuracy, responsiveness, compliance with specifications
            </Typography>
            <TextField label="Rating Notes" fullWidth multiline rows={3}
              value={rateData.rating_notes}
              onChange={(e) => setRateData({ ...rateData, rating_notes: e.target.value })}
              helperText="Justify the rating — used in future vendor selection decisions" />
            <TextField label="Next Review Date" type="date" fullWidth
              InputLabelProps={{ shrink: true }} value={rateData.next_review_date}
              onChange={(e) => setRateData({ ...rateData, next_review_date: e.target.value })}
              helperText="Set the next scheduled evaluation date" />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeAll}>Cancel</Button>
          <Button onClick={handleRate} variant="contained" color="warning">
            Save Rating
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── DELETE CONFIRM ────────────────────────────────────────────────── */}
      <Dialog open={deleteOpen} onClose={closeAll} maxWidth="xs" fullWidth>
        <DialogTitle>Delete Vendor</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete <strong>{selectedVendor?.vendor_name}</strong>?
            This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeAll}>Cancel</Button>
          <Button onClick={handleDelete} color="error" variant="contained">Delete</Button>
        </DialogActions>
      </Dialog>

      {/* ── IMPORT DIALOG ─────────────────────────────────────────────────── */}
      <VendorImportDialog
        open={importOpen}
        onClose={() => setImportOpen(false)}
        onImported={() => { fetchVendors(); }}
      />
    </>
  );
};

export default Vendor;
